import { Timer, Gauge, ShieldCheck, Cpu, Boxes } from "lucide-react";
import { Reveal, Kicker } from "../lib/motion";

const PILLARS = [
  {
    icon: Timer,
    title: "Faster Close",
    text: "AI-assisted reconciliation, anomaly detection and document processing help reduce close time and manual errors.",
  },
  {
    icon: Gauge,
    title: "Real-Time Visibility",
    text: "Dashboards and reporting provide better visibility into cash, runway, revenue, margins, KPIs, unit economics and business performance.",
  },
  {
    icon: ShieldCheck,
    title: "Intelligent Compliance",
    text: "Automation around GST, TDS, payroll, ROC / secretarial, compliance calendars and overseas filings helps reduce missed deadlines and compliance risk.",
  },
  {
    icon: Cpu,
    title: "AI Tools & Accelerators",
    text: "Custom AI tools built by the Cogniti.ai CFO team for finance operations — including invoice processing, reconciliations, close support, GST / TDS workflows, management dashboards, exception monitoring and finance automation accelerators.",
  },
  {
    icon: Boxes,
    title: "Platform Fluency",
    text: "Hands-on experience with modern cloud-finance architecture and leading finance systems.",
    platforms: ["Zoho Books", "NetSuite", "Zuora"],
  },
];

export const AIAdvantage = () => (
  <section data-testid="ai-advantage-section" className="relative overflow-hidden bg-navy py-24 md:py-32">
    <div aria-hidden="true" className="absolute inset-0 dark-grid-bg" />
    <div
      aria-hidden="true"
      className="pointer-events-none absolute -top-40 left-1/2 h-[480px] w-[720px] -translate-x-1/2 rounded-full bg-[radial-gradient(circle,rgba(245,166,35,0.14),transparent_65%)]"
    />
    <div
      aria-hidden="true"
      className="pointer-events-none absolute bottom-0 right-0 font-display text-[16rem] font-extrabold leading-none text-outline opacity-40 select-none hidden xl:block"
    >
      AI
    </div>

    <div className="relative max-w-7xl mx-auto px-6 md:px-12">
      <Reveal className="max-w-3xl">
        <Kicker dark>The AI Advantage</Kicker>
        <h2 className="mt-6 font-display text-4xl md:text-5xl font-bold tracking-tight text-white leading-[1.08]">
          AI-enabled finance.
          <span className="block text-brand">Real business impact.</span>
        </h2>
        <p className="mt-6 text-base md:text-lg leading-relaxed text-white/65">
          Cogniti.ai CFO applies AI, automation and modern finance technology
          across CFO Services, Finance Shared Services, Controllership and
          Compliance. We also build customised AI tools for finance workflows —
          including reporting, reconciliations, invoice processing, compliance
          tracking, dashboards and management visibility — helping finance teams
          reduce repetitive work, improve accuracy and access decision-ready
          information faster.
        </p>
      </Reveal>

      <div className="mt-14 grid gap-5 md:grid-cols-2 lg:grid-cols-3">
        {PILLARS.map((p, i) => (
          <Reveal key={p.title} delay={(i % 3) * 0.1} className={i === 4 ? "md:col-span-2 lg:col-span-1" : ""}>
            <article
              data-testid={`ai-pillar-${i}`}
              className="group h-full rounded-xl border border-white/10 bg-white/[0.04] p-8 backdrop-blur-sm transition-[transform,background-color,border-color] duration-300 hover:-translate-y-1.5 hover:border-brand/40 hover:bg-white/[0.07]"
            >
              <div className="flex h-11 w-11 items-center justify-center rounded-lg bg-brand/15 text-brand">
                <p.icon size={20} strokeWidth={1.8} />
              </div>
              <h3 className="mt-6 font-display text-xl font-bold text-white">{p.title}</h3>
              <p className="mt-3 text-sm leading-relaxed text-white/60">{p.text}</p>
              {p.platforms && (
                <div data-testid="platform-fluency-strip" className="mt-5 flex flex-wrap gap-2">
                  {p.platforms.map((pl) => (
                    <span key={pl} className="rounded-md border border-brand/30 bg-brand/10 px-3.5 py-1.5 text-xs font-bold tracking-wide text-brand">
                      {pl}
                    </span>
                  ))}
                </div>
              )}
            </article>
          </Reveal>
        ))}
      </div>

      <Reveal delay={0.15} className="mt-20 text-center">
        <p className="font-editorial italic text-2xl md:text-4xl text-white/85 leading-snug">
          Less time processing information.
          <span className="block mt-1 font-display not-italic font-bold text-brand">More time using it.</span>
        </p>
      </Reveal>
    </div>
  </section>
);
